<template>
  <div id="roles-and-info-types">
    <Breadcrumbs
      :breadcrumbs="breadcrumbs">

      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <div v-show="!isCreatingOrUpdatingRole">
          <Searchable :querySearch.sync="querySearch"/>
        </div>
      </div>

    </Breadcrumbs>

    <div class="btn btn__floating btn__round__large" v-if="!isCreatingOrUpdatingRole">
      <va-button
        key="btn-create-role"
        color="success"
        @click="onClickAddRole">
        <i class="fa fa-plus"></i>
      </va-button>
    </div>

    <section v-if="isCreatingOrUpdatingRole">
      <RoleForm
        ref="roleForm"
        :title="this.selectedRoleForUpdate ? $t('roles.update_role') : $t('roles.create_role')"
        :role="selectedRoleForUpdate"
        :roles="roles"
        :infoTypes="infoTypes"
        @save="onClickSaveRole"
        @cancel="onClickCancelUpdateOrCreateRole"
      />
    </section>

    <section class="roles-wrapper pb-5" v-else-if="rolesInView.length > 0">
      <Role v-for="(role, index) in rolesInView" :key="index" :role="role">
        <template slot="after-name">
          <span>
            <va-popover
              placement="left"
              class="ml-3"
              :message="$t('roles.update_role')">
              <span class="icon hoverable clickable fa fa-pencil" @click="onClickEditRole(role)"></span>
            </va-popover>
          </span>

          <span>
            <va-popover
              placement="left"
              class="ml-3"
              :message="$t('roles.delete_role')">
              <span class="icon hoverable clickable fa fa-trash" @click="onClickDeleteRole(role)"></span>
            </va-popover>
          </span>
        </template>
      </Role>
    </section>

    <section v-else-if="isLoading" class="py-5 d-flex flex-column align-items-center">
      <span class="mb-3">{{$t('roles.is_loading')}}</span>
      <hollow-dots-spinner
        :animation-duration="1000"
        :size="60"
        color="#29c4a9"
      />
    </section>

    <section v-else class="text-center py-5">
      <span class="mb-3">{{$t('roles.no_roles_found')}}</span>
    </section>

    <ConfirmationModal
      v-if="isDeletingRole"
      :title="$t('roles.delete_role')"
      :body="$t('roles.confirm_delete_role', {name: selectedRoleForDelete.name})"
      @modal-confirmed="deleteRole"
      @modal-canceled="cancelDeleteRole"/>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { RoleService } from '@/api/AuthService'
import Errors from '@/utils/StandardErrors'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import Role from './Role'
import RoleForm from './RoleForm'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import Searchable from '@/components/common/Searchable'
import { HollowDotsSpinner } from 'epic-spinners'

export default {
  components: {
    Breadcrumbs,
    Role,
    RoleForm,
    Searchable,
    HollowDotsSpinner,
    ConfirmationModal
  },
  computed: {
    breadcrumbs: function () {
      return [
        { title: this.$t('menu.configurations') },
        { title: this.$t('menu.roles'), active: true },
      ]
    },
    rolesInView: function () {
      if (this.querySearch && this.querySearch.trim() !== '') {
        return this.roles.filter(role => role.name.toLowerCase().includes(this.querySearch.toLowerCase()))
      } else {
        return this.roles
      }
    }
  },
  data: function () {
    return {
      roles: [],
      infoTypes: [],
      isLoading: true,
      isCreatingOrUpdatingRole: false,
      isDeletingRole: false,
      selectedRoleForUpdate: null,
      selectedRoleForDelete: null,
      querySearch: ''
    }
  },
  created: function () {
    Promise.all([RoleService.getRoles(), RoleService.getInfoTypes()])
      .then(values => {
        this.setRoleList(values[0])
        this.setInfoTypeList(values[1])
        this.isLoading = false
      })
      .catch(errors => {
        console.log(errors)
        this.isLoading = false
      })
  },
  methods: {
    onClickAddRole: function () {
      this.isCreatingOrUpdatingRole = true
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onClickEditRole: function (role) {
      this.isCreatingOrUpdatingRole = true
      this.selectedRoleForUpdate = role
    },
    onClickDeleteRole: function (role) {
      this.isDeletingRole = true
      this.selectedRoleForDelete = role
    },
    onClickCancelUpdateOrCreateRole: function () {
      this.isCreatingOrUpdatingRole = false
      this.selectedRoleForUpdate = null
    },
    cancelDeleteRole: function () {
      this.isDeletingRole = false
      this.selectedRoleForDelete = null
    },
    onClickSaveRole: function () {
      let formData = this.$refs.roleForm.getFormData()
      if (this.selectedRoleForUpdate === null) {
        this.createRole(formData)
      } else {
        this.updateRole(formData)
      }
    },
    setRoleList: function (roles) {
      this.roles = roles
      this.roles.sort((a, b) => a.name.localeCompare(b.name))
    },
    addRoleToList: function (role) {
      this.roles.push(role)
      this.roles.sort((a, b) => a.name.localeCompare(b.name))
    },
    updateRoleInList: function (role) {
      this.roles = this.roles.filter(r => r.id !== role.id)
      this.addRoleToList(role)
    },
    deleteRoleInList: function (role) {
      this.roles = this.roles.filter(r => r.id !== role.id)
    },
    setInfoTypeList: function (infoTypes) {
      this.infoTypes = infoTypes
    },
    createRole: function (formData) {
      RoleService.store(formData)
        .then(role => {
          if (formData.subroles.length > 0) {
            RoleService.updateSubRoles(role.id, formData)
              .then(updatedRole => {
                this.showToast(this.$t('roles.role_created'), this.$store.state.successToastOptions)
                this.addRoleToList(updatedRole)
                this.onClickCancelUpdateOrCreateRole()
              })
          } else {
            this.showToast(this.$t('roles.role_created'), this.$store.state.successToastOptions)
            this.addRoleToList(role)
            this.onClickCancelUpdateOrCreateRole()
          }
        })
        .catch(errors => {
          const status = errors.response && errors.response.hasOwnProperty('status') ? errors.response.status : -1
          switch (status) {
          case 422:
            this.showToast(this.$t('roles.form_contains_errors'), this.$store.state.errorToastOptions)
            this.$refs.roleForm.setFormErrors(errors.response.data.errors)
            break
          default:
            this.showToast(this.$t('roles.something_went_wrong'), this.$store.state.errorToastOptions)
            break
          }
        })
    },
    updateRole: function (formData) {
      RoleService.update(this.selectedRoleForUpdate.id, formData)
        .then(role => {
          let currentSubRoles = this.selectedRoleForUpdate.subRoles.map(subRole => subRole.id).sort((a, b) => { return b - a })
          let newSubRoles = formData.subroles.sort((a, b) => { return b - a })
          if (currentSubRoles.join(',').localeCompare(newSubRoles.join(',')) !== 0) {
            RoleService.updateSubRoles(role.id, { subroles: newSubRoles })
              .then(updatedRole => {
                this.showToast(this.$t('roles.role_updated'), this.$store.state.successToastOptions)
                this.updateRoleInList(updatedRole)
                this.onClickCancelUpdateOrCreateRole()
              })
          } else {
            this.showToast(this.$t('roles.role_updated'), this.$store.state.successToastOptions)
            this.updateRoleInList(role)
            this.onClickCancelUpdateOrCreateRole()
          }
        })
        .catch(errors => {
          console.log(errors)
          const status = errors.response && errors.response.hasOwnProperty('status') ? errors.response.status : -1
          switch (status) {
          case 422:
            this.showToast(this.$t('roles.form_contains_errors'), this.$store.state.errorToastOptions)
            this.$refs.roleForm.setFormErrors(errors.response.data.errors)
            break
          default:
            this.showToast(this.$t('roles.something_went_wrong'), this.$store.state.errorToastOptions)
            break
          }
        })
    },
    deleteRole: function () {
      RoleService.delete(this.selectedRoleForDelete.id)
        .then(response => {
          this.showToast(this.$t('roles.role_deleted'), this.$store.state.successToastOptions)
          this.deleteRoleInList(this.selectedRoleForDelete)
          this.cancelDeleteRole()
        })
        .catch(errors => {
          this.showToast(this.$t('roles.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    }
  }
}
</script>

<style lang="scss">

</style>
